import { ReactElement, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import NewButton from "../../../components/Atomos/Buttons/newButton";
import TableHorizontalScrollContainer from "../../../components/Organismos/Table/TableHorizontalScrollContainer";
import { ManageDealRow } from "../../../Services/ManageDealmaker";

const PAGE_SIZE = 10;

type SortKey = "title" | "dealType" | "valueRank" | "postedSort" | "approved";

interface ManageDealmakerTableProps {
  title: string;
  rows: ManageDealRow[];
  archived: boolean;
  exportTitle: string;
}

function compareRows(left: ManageDealRow, right: ManageDealRow, key: SortKey, direction: number): number {
  const leftValue = left[key];
  const rightValue = right[key];
  if (typeof leftValue === "number" && typeof rightValue === "number") {
    return (leftValue - rightValue) * direction;
  }
  return String(leftValue).localeCompare(String(rightValue)) * direction;
}

function csvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function downloadCsv(title: string, rows: ManageDealRow[]): void {
  const header = ["#", "Deal Title", "Deal Type", "Value", "Posted Date", "Approved"];
  const lines = rows.map((row, index) => [String(index + 1), row.title, row.dealType, row.dealAmount, row.postedDate, row.approved].map(csvCell).join(","));
  const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${title}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function ManageDealmakerTable({ title, rows, archived, exportTitle }: ManageDealmakerTableProps): ReactElement {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [direction, setDirection] = useState(1);
  const sorted = useMemo(() => {
    if (!sortKey) {
      return rows;
    }
    return [...rows].sort((left, right) => compareRows(left, right, sortKey, direction));
  }, [rows, sortKey, direction]);
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = sorted.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const from = sorted.length === 0 ? 0 : safePage * PAGE_SIZE + 1;
  const to = sorted.length === 0 ? 0 : safePage * PAGE_SIZE + visible.length;

  function toggleSort(key: SortKey): void {
    if (sortKey === key) {
      setDirection(direction * -1);
    } else {
      setSortKey(key);
      setDirection(1);
    }
    setPage(0);
  }

  function openDeal(dealId: number): void {
    window.open(`/dealmaker?action=view&dealid=${dealId}`, "Profile", "width=1050,height=800");
  }

  return (
    <section className="stack-tight">
      {archived ? <h3>{title}</h3> : <h2 className="section-bar">{title}</h2>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <NewButton text="Export to Excel" color="excel" size="small" onClick={() => downloadCsv(exportTitle, sorted)} />
          <p className="form-status">Showing {from} to {to} of {sorted.length} entries</p>
        </div>
        <div className="flex gap-2">
          <NewButton text="First" style="outlined" size="small" disabled={safePage === 0} onClick={() => setPage(0)} />
          <NewButton text="Prev" style="outlined" size="small" disabled={safePage === 0} onClick={() => setPage(Math.max(0, safePage - 1))} />
          <NewButton text="Next" style="outlined" size="small" disabled={safePage >= pageCount - 1} onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))} />
          <NewButton text="Last" style="outlined" size="small" disabled={safePage >= pageCount - 1} onClick={() => setPage(pageCount - 1)} />
        </div>
      </div>
      <div className="overflow-hidden border border-gray-200 bg-white">
        <TableHorizontalScrollContainer measureKey={visible.length}>
          <table className="manage-table min-w-full border-collapse text-sm text-gray-font">
            <thead>
              <tr>
                <th>#</th>
                <th><button type="button" onClick={() => toggleSort("title")}>Deal Title</button></th>
                <th><button type="button" onClick={() => toggleSort("dealType")}>Deal Type</button></th>
                <th><button type="button" onClick={() => toggleSort("valueRank")}>Value</button></th>
                <th><button type="button" onClick={() => toggleSort("postedSort")}>Posted Date</button></th>
                <th><button type="button" onClick={() => toggleSort("approved")}>Approved</button></th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={7}>No data available in table</td>
                </tr>
              ) : visible.map((row, index) => (
                <tr key={row.dealId}>
                  <td>{safePage * PAGE_SIZE + index + 1}</td>
                  <td><button type="button" className="text-left text-primary underline" onClick={() => openDeal(row.dealId)}>{row.title}</button></td>
                  <td>{row.dealType}</td>
                  <td>{row.dealAmount}</td>
                  <td>{row.postedDate}</td>
                  <td>{row.approved}</td>
                  <td>
                    <div className="flex flex-wrap gap-2">
                      <NewButton text="Update" size="small" disabled={archived} onClick={() => navigate(`/manage_dealmaker?action=update&id=${row.dealId}`)} />
                      <NewButton text="View" size="small" style="outlined" onClick={() => openDeal(row.dealId)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableHorizontalScrollContainer>
      </div>
    </section>
  );
}
